import { useState } from 'react'
import {
  Stack, Text, Group, Badge, Alert, Skeleton, Table, Paper, Tabs, MultiSelect,
  SegmentedControl, Code, Loader, SimpleGrid,
} from '@mantine/core'
import { IconLock, IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useCurrentTenant } from '@/store/auth'
import { formatApiError } from '@/api/errors'
import {
  explicarMotivo, lerTrilha, listarBackups, listarClusters,
  type ClusterCloudHsm,
} from '@/api/cloudhsm'

/**
 * Tela "CloudHSM" (Segurança) — SÓ LEITURA.
 *
 * Nenhum botão muda o cluster: usuários e chaves do HSM são do admin do HSM, que os
 * administra com o `cloudhsm-cli`. O que esta tela acrescenta é ver o cluster, os HSMs,
 * os backups e a trilha de auditoria que o próprio HSM escreveu.
 *
 * Regra que não pode cair: região que não foi lida NÃO é região sem cluster. Com a
 * role sem `cloudhsm:DescribeClusters`, a AWS recusa; dizer "nenhum cluster" aí seria
 * afirmar algo sobre a conta do cliente sem ter olhado.
 */

const REGIOES_SEMPRE_OFERECIDAS = ['us-east-1', 'us-east-2']
const HORAS = ['1', '6', '24', '72', '168']
const LIMITE_DA_TRILHA = 200

function quando(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('pt-BR')
}

function corDoEstado(estado: string): string {
  if (estado === 'ACTIVE') return 'green'
  if (estado.includes('FAILED') || estado === 'DEGRADED') return 'red'
  if (estado.includes('DELETE')) return 'gray'
  return 'yellow'
}

/** O backend responde 403/409/503 com `detail` conhecido; o resto cai no genérico. */
function erroDaLeitura(err: unknown): string {
  const e = err as { response?: { status?: number; data?: { detail?: unknown } } }
  const status = e?.response?.status
  const detail = e?.response?.data?.detail
  if (status === 403) return 'Você não tem a permissão cloudhsm:view neste tenant.'
  if (status === 409) return 'Este tenant ainda não tem conta AWS associada.'
  if (status === 503 && (detail as { motivo?: string } | undefined)?.motivo === 'tenant_assume_role_failed') {
    return 'Não foi possível assumir a role de leitura na conta deste tenant.'
  }
  return formatApiError(err, 'Não foi possível consultar o CloudHSM.')
}

export function CloudHsmPage() {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  // Vazio = as regiões do tenant (o backend decide).
  const [regioes, setRegioes] = useState<string[]>([])

  const consulta = useQuery({
    queryKey: ['cloudhsm', 'clusters', tenantId, regioes.join(',')],
    queryFn: () => listarClusters(tenantId!, regioes),
    enabled: !!tenantId,
    retry: false,
    staleTime: 60_000,
  })

  if (!tenantId) {
    return <Text c="dimmed">Selecione um tenant para ver o CloudHSM.</Text>
  }

  const dados = consulta.data
  const opcoes = [...new Set([...(dados?.regioes_do_tenant ?? []), ...REGIOES_SEMPRE_OFERECIDAS, ...regioes])]
  const falhas = Object.entries(dados?.regioes_com_falha ?? {})
  const lidas = dados?.regioes_lidas ?? []

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end">
        <Stack gap={2}>
          <Text size="lg" fw={700}>
            <IconLock size={20} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            CloudHSM
          </Text>
          <Text size="sm" c="dimmed">
            Somente leitura: clusters, HSMs, backups e a trilha de auditoria do HSM na conta do tenant.
          </Text>
        </Stack>
        <MultiSelect
          label="Regiões"
          placeholder={regioes.length === 0 ? 'Regiões do tenant' : undefined}
          data={opcoes}
          value={regioes}
          onChange={setRegioes}
          clearable
          w={320}
        />
      </Group>

      <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
        Usuários e chaves do HSM são administrados pelo admin do HSM com o cloudhsm-cli, fora do
        ConsoleAI. Aqui aparece a trilha do que ele fez, como o HSM a registrou.
      </Alert>

      {consulta.isLoading && <Skeleton h={160} />}

      {consulta.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Não foi possível consultar o CloudHSM">
          {erroDaLeitura(consulta.error)}
        </Alert>
      )}

      {dados && (
        <>
          <Group gap="xs">
            <Text size="sm" c="dimmed">Regiões consultadas:</Text>
            {dados.regioes_consultadas.length === 0 && <Text size="sm" c="dimmed">nenhuma</Text>}
            {dados.regioes_consultadas.map((r) => (
              <Badge key={r} variant="light" color={dados.regioes_com_falha[r] ? 'red' : 'green'}>
                {r}{dados.regioes_com_falha[r] ? ' — não lida' : ''}
              </Badge>
            ))}
          </Group>

          {falhas.map(([regiao, motivo]) => (
            <Alert key={regiao} color="orange" icon={<IconAlertTriangle size={16} />}
              title={`Região ${regiao} não foi lida`}>
              Não deu para ver se há CloudHSM em {regiao}: {explicarMotivo(motivo)}.
            </Alert>
          ))}

          {dados.clusters.length === 0 && lidas.length > 0 && (
            <Text c="dimmed">
              Nenhum cluster de CloudHSM {lidas.length === 1 ? 'na região lida' : 'nas regiões lidas'}: {lidas.join(', ')}.
            </Text>
          )}

          {dados.clusters.map((c) => (
            <CartaoDoCluster key={`${c.regiao}/${c.cluster_id}`} tenantId={tenantId} cluster={c} />
          ))}
        </>
      )}
    </Stack>
  )
}

function Campo({ rotulo, valor }: { rotulo: string; valor: string | null | undefined }) {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed">{rotulo}</Text>
      <Text size="sm" ff="monospace">{valor || '—'}</Text>
    </Stack>
  )
}

function CartaoDoCluster({ tenantId, cluster }: { tenantId: number; cluster: ClusterCloudHsm }) {
  const [aba, setAba] = useState<string | null>('hsms')
  const subnets = Object.entries(cluster.subnets)
  const retencao = cluster.retencao_de_backup

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="md">
        <Group justify="space-between">
          <Group gap="xs">
            <Text fw={700} ff="monospace">{cluster.cluster_id}</Text>
            <Badge color={corDoEstado(cluster.estado)} variant="light">{cluster.estado}</Badge>
            {cluster.modo && (
              <Badge color={cluster.modo === 'FIPS' ? 'blue' : 'gray'} variant="outline">{cluster.modo}</Badge>
            )}
          </Group>
          <Text size="sm" c="dimmed">{cluster.regiao}</Text>
        </Group>
        {cluster.mensagem_de_estado && <Text size="sm" c="dimmed">{cluster.mensagem_de_estado}</Text>}

        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
          <Campo rotulo="Tipo de HSM" valor={cluster.tipo_de_hsm} />
          <Campo rotulo="VPC" valor={cluster.vpc_id} />
          <Campo rotulo="Security group" valor={cluster.security_group} />
          <Campo rotulo="Criado em" valor={quando(cluster.criado_em)} />
          <Campo rotulo="Subnets" valor={subnets.map(([az, s]) => `${az}: ${s}`).join(', ')} />
          <Campo rotulo="Retenção de backup" valor={retencao ? `${retencao.valor} (${retencao.tipo})` : null} />
        </SimpleGrid>

        <Tabs value={aba} onChange={setAba}>
          <Tabs.List>
            <Tabs.Tab value="hsms">HSMs ({cluster.hsms.length})</Tabs.Tab>
            <Tabs.Tab value="backups">Backups</Tabs.Tab>
            <Tabs.Tab value="trilha">Trilha do HSM</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="hsms" pt="sm"><TabelaDeHsms cluster={cluster} /></Tabs.Panel>
          <Tabs.Panel value="backups" pt="sm">
            {aba === 'backups' && <Backups tenantId={tenantId} cluster={cluster} />}
          </Tabs.Panel>
          <Tabs.Panel value="trilha" pt="sm">
            {aba === 'trilha' && <Trilha tenantId={tenantId} cluster={cluster} />}
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Paper>
  )
}

function TabelaDeHsms({ cluster }: { cluster: ClusterCloudHsm }) {
  if (cluster.hsms.length === 0) {
    return <Text size="sm" c="dimmed">Este cluster não tem HSM.</Text>
  }
  return (
    <Table striped withTableBorder>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>HSM</Table.Th><Table.Th>Estado</Table.Th><Table.Th>Zona</Table.Th>
          <Table.Th>Subnet</Table.Th><Table.Th>ENI</Table.Th><Table.Th>IP</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {cluster.hsms.map((h) => (
          <Table.Tr key={h.hsm_id}>
            <Table.Td ff="monospace">{h.hsm_id}</Table.Td>
            <Table.Td>
              <Badge color={corDoEstado(h.estado)} variant="light">{h.estado}</Badge>
              {h.mensagem_de_estado && <Text size="xs" c="dimmed">{h.mensagem_de_estado}</Text>}
            </Table.Td>
            <Table.Td>{h.zona ?? '—'}</Table.Td>
            <Table.Td ff="monospace">{h.subnet_id ?? '—'}</Table.Td>
            <Table.Td ff="monospace">{h.eni_id ?? '—'}</Table.Td>
            <Table.Td ff="monospace">{h.ip ?? '—'}</Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  )
}

function Backups({ tenantId, cluster }: { tenantId: number; cluster: ClusterCloudHsm }) {
  const q = useQuery({
    queryKey: ['cloudhsm', 'backups', tenantId, cluster.regiao, cluster.cluster_id],
    queryFn: () => listarBackups(tenantId, cluster.cluster_id, cluster.regiao),
    retry: false,
    staleTime: 60_000,
  })
  if (q.isLoading) return <Loader size="sm" />
  if (q.isError) return <Alert color="red">{erroDaLeitura(q.error)}</Alert>
  const d = q.data!
  if (d.falha) {
    return <Alert color="orange">Os backups não foram lidos: {explicarMotivo(d.falha)}.</Alert>
  }
  if (d.backups.length === 0) return <Text size="sm" c="dimmed">Este cluster não tem backup.</Text>
  return (
    <Table striped withTableBorder>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Backup</Table.Th><Table.Th>Estado</Table.Th><Table.Th>Criado em</Table.Th>
          <Table.Th>Expira</Table.Th><Table.Th>Origem</Table.Th><Table.Th>Apagado em</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {d.backups.map((b) => (
          <Table.Tr key={b.backup_id}>
            <Table.Td ff="monospace">{b.backup_id}</Table.Td>
            <Table.Td><Badge color={corDoEstado(b.estado)} variant="light">{b.estado}</Badge></Table.Td>
            <Table.Td>{quando(b.criado_em)}</Table.Td>
            <Table.Td>{b.nunca_expira ? 'Nunca expira' : 'Segue a retenção'}</Table.Td>
            <Table.Td>
              {b.backup_de_origem
                ? `cópia de ${b.backup_de_origem} (${b.regiao_de_origem ?? '—'}) em ${quando(b.copiado_em)}`
                : '—'}
            </Table.Td>
            <Table.Td>{quando(b.apagado_em)}</Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  )
}

function Trilha({ tenantId, cluster }: { tenantId: number; cluster: ClusterCloudHsm }) {
  const [horas, setHoras] = useState('24')
  const q = useQuery({
    queryKey: ['cloudhsm', 'trilha', tenantId, cluster.regiao, cluster.cluster_id, horas],
    queryFn: () => lerTrilha(tenantId, cluster.cluster_id, cluster.regiao, Number(horas), LIMITE_DA_TRILHA),
    retry: false,
    staleTime: 30_000,
  })
  const d = q.data

  return (
    <Stack gap="sm">
      <Text size="sm" c="dimmed">
        O que o próprio HSM registrou — login, criação de usuário, geração de chave —, feito pelo
        admin do HSM com o cloudhsm-cli. A mensagem aparece como o HSM a escreveu.
      </Text>
      <Group gap="xs">
        <Text size="sm">Últimas</Text>
        <SegmentedControl size="xs" data={HORAS.map((h) => ({ value: h, label: `${h} h` }))}
          value={horas} onChange={setHoras} />
      </Group>

      {q.isLoading && <Loader size="sm" />}
      {q.isError && <Alert color="red">{erroDaLeitura(q.error)}</Alert>}
      {d && d.falha && (
        <Alert color="orange">A trilha não foi lida: {explicarMotivo(d.falha)}.</Alert>
      )}
      {d && !d.falha && d.log_group_existe === false && (
        <Text size="sm" c="dimmed">
          O HSM ainda não escreveu trilha (log group <Code>{d.log_group}</Code> não existe).
        </Text>
      )}
      {d && d.truncada && (
        <Alert color="yellow">Há mais eventos no período: mostrando os {d.eventos.length} mais recentes.</Alert>
      )}
      {d && d.janela_incompleta && (
        <Alert color="yellow">
          Janela grande demais, reduza o período: a leitura parou antes do fim e os eventos mostrados
          podem não ser os mais recentes.
        </Alert>
      )}
      {d && !d.falha && d.log_group_existe === true && d.eventos.length === 0 && (
        <Text size="sm" c="dimmed">Nenhum evento nas últimas {d.horas} h.</Text>
      )}
      {d && d.eventos.length > 0 && (
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr><Table.Th>Quando</Table.Th><Table.Th>HSM</Table.Th><Table.Th>Mensagem</Table.Th></Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {d.eventos.map((e, i) => (
              <Table.Tr key={`${e.quando}-${i}`}>
                <Table.Td style={{ whiteSpace: 'nowrap' }}>{quando(e.quando)}</Table.Td>
                <Table.Td ff="monospace">{e.hsm ?? '—'}</Table.Td>
                <Table.Td>
                  <Code block style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{e.mensagem}</Code>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  )
}
